import apiClient from './apiClient';

/**
 * User Profile API Service
 * Handles user profile updates and onboarding completion
 */

/**
 * Submit user profile data (name, age, sex)
 * Completes the onboarding process
 * 
 * @param {Object} profileData - User profile information
 * @param {string} profileData.name - User's full name
 * @param {number} profileData.age - User's age
 * @param {string} profileData.sex - User's gender
 * @returns {Promise<Object>} Updated user object with onboarding_completed: true
 */
export const submitProfile = async (profileData) => {
  try {
    const response = await apiClient.post('/api/user/profile', {
      name: profileData.name,
      age: profileData.age,
      sex: profileData.sex
    });

    return {
      success: true,
      data: response.data
    };
  } catch (error) {
    console.error('Profile submission error:', error);

    // Extract meaningful error message
    let errorMessage = 'Failed to complete profile';
    
    if (error.response?.data?.message) {
      errorMessage = error.response.data.message;
    } else if (error.response?.status === 400) {
      errorMessage = 'Invalid profile data. Please check your information.';
    } else if (error.response?.status === 401) {
      errorMessage = 'Your session has expired. Please login again.';
    } else if (error.response?.status === 409) {
      errorMessage = 'Profile already exists. Please refresh the page.';
    }

    return {
      success: false,
      error: errorMessage
    };
  }
};

/**
 * Get current user profile
 * @returns {Promise<Object>} Current user profile
 */
export const getUserProfile = async () => {
  try {
    const response = await apiClient.get('/api/user/profile');
    return {
      success: true,
      data: response.data
    };
  } catch (error) {
    console.error('Get profile error:', error);
    return {
      success: false,
      error: error.message
    };
  }
};

/**
 * Update user profile (for future use)
 * @param {Object} updateData - Fields to update
 * @returns {Promise<Object>} Updated user profile
 */
export const updateProfile = async (updateData) => {
  try {
    const response = await apiClient.put('/api/user/profile', updateData);
    return {
      success: true,
      data: response.data
    };
  } catch (error) {
    console.error('Update profile error:', error);
    return {
      success: false,
      error: error.message
    };
  }
};

const userApi = {
  submitProfile,
  getUserProfile,
  updateProfile
};

export default userApi;
